import React from "react";
import { Card } from "../../../design-system";

const STATUS_META = {
  ok:       { tone: "success" },
  incident: { tone: "warning" },
  review:   { tone: "danger" },
  current:  { tone: "info" },
};

function pad(n) { return String(n).padStart(2, "0"); }
function fmtDur(min) {
  if (min == null) return "—";
  const sign = min < 0 ? "-" : "";
  const abs = Math.abs(min);
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}
function diffStr(diff) {
  if (diff == null) return "—";
  const sign = diff >= 0 ? "+" : "−";
  const abs = Math.abs(diff);
  return `${sign}${Math.floor(abs / 60)}h ${abs % 60}m`;
}
function dayOfWeekIdx(ymd) {
  const [y, m, d] = ymd.split("-").map((v) => parseInt(v, 10));
  return (new Date(y, m - 1, d).getDay() + 6) % 7;
}

/**
 * Vista calendario mensual de jornadas. Se usa tanto en el Historial
 * personal (`/jornada/historial`) como en la vista administrativa
 * (`/rrhh/jornadas`). Recibe el array de días ya enriquecidos por
 * `classifyDay` (los campos `status`, `inStr`, `outStr`, `worked_minutes`,
 * `planned_minutes`, `balance`, `dayNumber`, `isWeekend`, `noPlan`,
 * `isFuture` deben venir resueltos por el padre).
 */
export function CalendarioMes({ data, onOpenDay }) {
  if (!data?.length) return null;
  const firstDow = dayOfWeekIdx(data[0].date);
  const cells = Array(firstDow).fill(null).concat(data);
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <Card padded={false}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid var(--kr-border)", background: "var(--kr-neutral-50)" }}>
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
          <div key={d} style={{ padding: "10px 14px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600, color: "var(--kr-fg-muted)" }}>{d}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {weeks.flat().map((d, i) => (
          <CeldaMes
            key={i}
            day={d}
            onOpen={() => d && d.record && onOpenDay && onOpenDay(d)}
          />
        ))}
      </div>
    </Card>
  );
}

function CeldaMes({ day, onOpen }) {
  if (!day) {
    return <div style={{ minHeight: 96, borderRight: "1px solid var(--kr-border-subtle)", borderBottom: "1px solid var(--kr-border-subtle)", background: "var(--kr-neutral-50)" }} />;
  }
  const meta = day.status ? STATUS_META[day.status] : null;
  const inactive = day.isWeekend || day.noPlan || day.isFuture;
  const clickable = !!day.record;

  return (
    <div
      onClick={clickable ? onOpen : undefined}
      style={{
        minHeight: 96,
        padding: 10,
        borderRight: "1px solid var(--kr-border-subtle)",
        borderBottom: "1px solid var(--kr-border-subtle)",
        background: inactive ? "var(--kr-neutral-50)" : "#fff",
        cursor: clickable ? "pointer" : "default",
        opacity: day.isFuture ? 0.5 : 1,
        position: "relative",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, fontWeight: 700, color: day.status === "current" ? "var(--kr-action)" : "var(--kr-fg-strong)" }}>{pad(day.dayNumber)}</span>
        {meta && (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background:
                meta.tone === "success"
                  ? "var(--kr-success-500)"
                  : meta.tone === "warning"
                  ? "var(--kr-warning-500)"
                  : meta.tone === "danger"
                  ? "var(--kr-danger-500)"
                  : "var(--kr-info-500)",
            }}
          />
        )}
      </div>
      {day.worked_minutes > 0 && (
        <div>
          <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-strong)" }}>{day.inStr} – {day.outStr}</div>
          <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            {fmtDur(day.worked_minutes)} h
            {day.planned_minutes > 0 && (
              <span style={{ color: day.balance >= 0 ? "var(--kr-success-700)" : "var(--kr-warning-700)" }}>
                {" · "}{diffStr(day.balance)}
              </span>
            )}
          </div>
        </div>
      )}
      {day.status === "review" && (
        <div style={{ fontSize: 11, color: "var(--kr-danger-700)", fontWeight: 600 }}>Sin marcaje</div>
      )}
    </div>
  );
}

export default CalendarioMes;
